import React from "react";
import { FaStar, FaClock, FaComments, FaBox, FaRegComment } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const StarRating = ({ value = 0, size = 16 }) => {
  const rounded = Math.round(value);
  return (
    <div className="d-flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <FaStar
          key={n}
          size={size}
          color={n <= rounded ? "#f5b301" : "#e4e7ec"}
        />
      ))}
    </div>
  );
};

// لون حسب التقييم
const getScoreColor = (score) => {
  if (score >= 4) return "#0d9f6e";      // أخضر
  if (score >= 3) return "#d97706";      // برتقالي
  return "#dc2626";                       // أحمر
};

const getScoreBg = (score) => {
  if (score >= 4) return "#f0fdf9";
  if (score >= 3) return "#fffbeb";
  return "#fef2f2";
};

const CriteriaRow = ({ icon: Icon, label, value }) => {
  const color = getScoreColor(value);
  const bg = getScoreBg(value);

  return (
    <div
      className="d-flex align-items-center gap-3 p-2 rounded-2 flex-wrap"
      style={{ background: bg }}
    >
      <div
        className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 32, height: 32, background: "#fff" }}
      >
        <Icon size={14} color={color} />
      </div>
      <div className="flex-grow-1">
        <div
          className="fw-semibold"
          style={{ fontSize: 13, color: "#101828" }}
        >
          {label}
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        <StarRating value={value} size={13} />
        <span
          className="fw-bold"
          style={{ fontSize: 13, color, minWidth: 24, textAlign: "right" }}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

export default function DriverRatingDisplay({ rating, comment }) {
  const { t } = useTranslation();

  if (!rating) return null;

  const overall = rating.average ?? rating.value ?? 0;
  const overallColor = getScoreColor(overall);

  return (
    <div
      className="mt-3 p-3 rounded-3 border"
      style={{ background: "#fafbfc", borderColor: "#e4e7ec" }}
    >
      {/* Header: العنوان + التقييم العام */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              background: overallColor,
              color: "#fff",
            }}
          >
            <FaStar size={16} />
          </div>
          <div>
            <div className="fw-bold" style={{ fontSize: 14, color: "#101828" }}>
              {t("rating.display_title", "Kundenbewertung")}
            </div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              {t("rating.display_subtitle", "Bewertung dieses Auftrags")}
            </div>
          </div>
        </div>

        {/* Overall score badge */}
        <div
          className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
          style={{ background: overallColor, color: "#fff" }}
        >
          <FaStar size={12} />
          <span className="fw-bold" style={{ fontSize: 14 }}>
            {overall.toFixed(1)}
          </span>
        </div>
      </div>

      {/* Criteria */}
      <div className="d-flex flex-column gap-2 mb-3">
        <CriteriaRow
          icon={FaClock}
          label={t("rating.criteria.punctuality.title", "Pünktlichkeit")}
          value={rating.punctuality ?? 0}
        />
        <CriteriaRow
          icon={FaComments}
          label={t("rating.criteria.communication.title", "Kommunikation")}
          value={rating.communication ?? 0}
        />
        <CriteriaRow
          icon={FaBox}
          label={t("rating.criteria.cargo_care.title", "Umgang mit der Ware")}
          value={rating.cargo_care ?? 0}
        />
        <CriteriaRow
          icon={FaStar}
          label={t("rating.criteria.rating.title", "Umgang mit der Ware")}
          value={rating.rating ?? 0}
        />
      </div>

      {/* Comment */}
      {comment && (
        <div
          className="p-3 rounded-2 d-flex gap-2"
          style={{ background: "#fff", border: "1px solid #e4e7ec" }}
        >
          <FaRegComment size={14} color="#667085" style={{ marginTop: 2 }} />
          <div
            className="flex-grow-1"
            style={{ fontSize: 13, color: "#475467", lineHeight: 1.5 }}
          >
            {comment}
          </div>
        </div>
      )}
    </div>
  );
}